import React from 'react';
import { Projection } from 'ol/proj';
import Geolocation from 'ol/Geolocation';

import { PlayerCoordinate } from '../types/state/player/player-state';

interface UseGeolocationTrackingOptions {
  enabled?: boolean;
  playerCode?: string;
  projection: Projection;
  smoothingAlpha?: number;
  noiseThresholdM?: number;
  maxAcceptableAccuracyM?: number;
  onFix: (coord: PlayerCoordinate) => void;
}

interface UseGeolocationTrackingResult {
  accuracy: number | undefined;
  lastCoordinate: PlayerCoordinate | null;
}

const smoothCoordinate = (
  prev: PlayerCoordinate,
  next: PlayerCoordinate,
  alpha: number
): PlayerCoordinate => {
  const prevAcc = prev.accuracy ?? undefined;
  const nextAcc = next.accuracy ?? undefined;

  const weight =
    prevAcc != null && nextAcc != null && prevAcc + nextAcc > 0
      ? Math.min(1, prevAcc / (prevAcc + nextAcc))
      : alpha;

  return {
    latitude:
      prev.latitude != null && next.latitude != null
        ? prev.latitude + weight * (next.latitude - prev.latitude)
        : next.latitude,
    longitude:
      prev.longitude != null && next.longitude != null
        ? prev.longitude + weight * (next.longitude - prev.longitude)
        : next.longitude,
    accuracy: nextAcc,
    heading: next.heading ?? prev.heading ?? null,
    altitude: next.altitude ?? prev.altitude ?? null
  };
};

export const useGeolocationTracking = ({
  projection,
  enabled = true,
  smoothingAlpha = 0.3,
  onFix
}: UseGeolocationTrackingOptions): UseGeolocationTrackingResult => {
  const geolocationRef = React.useRef<Geolocation | null>(null);
  const lastFixRef = React.useRef<PlayerCoordinate | null>(null);

  const [accuracy, setAccuracy] = React.useState<number | undefined>(undefined);
  const [lastCoordinate, setLastCoordinate] =
    React.useState<PlayerCoordinate | null>(null);

  React.useEffect(() => {
    const geolocation = new Geolocation({
      trackingOptions: {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 10000
      },
      tracking: enabled,
      projection
    });

    geolocationRef.current = geolocation;

    const onChange = () => setAccuracy(geolocation.getAccuracy() ?? undefined);

    const onPositionChange = () => {
      const position = geolocation.getPosition();
      if (position) {
        const fixAccuracy = geolocation.getAccuracy();

        const heading = geolocation.getHeading() ?? null;
        const altitude = geolocation.getAltitude() ?? null;

        const nextRaw: PlayerCoordinate = {
          longitude: position[0],
          latitude: position[1],
          accuracy: fixAccuracy ?? undefined,
          heading,
          altitude
        };

        const prev = lastFixRef.current;
        const smoothed = prev
          ? smoothCoordinate(prev, nextRaw, smoothingAlpha)
          : nextRaw;

        lastFixRef.current = smoothed;
        setLastCoordinate(smoothed);
        onFix(smoothed);
      }
    };

    geolocation.on('change', onChange);
    geolocation.on('change:position', onPositionChange);

    return () => {
      geolocation.un('change', onChange);
      geolocation.un('change:position', onPositionChange);
      geolocation.setTracking(false);
      geolocationRef.current = null;
    };
  }, [enabled, projection]);

  return { accuracy, lastCoordinate };
};
